import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { isTrustedDevice, fetchQuickStatus } from "@/lib/device";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, ShieldCheck, TrendingUp, Boxes } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (localStorage.getItem("neec_token") || sessionStorage.getItem("neec_full_login")) return;
    if (isTrustedDevice()) { nav("/quick", { replace: true }); return; }
    fetchQuickStatus().then((s) => { if (s.quick_login_available) { setServerTrusted(true); nav("/quick", { replace: true }); } });
  }, [nav]);
  const [serverTrusted, setServerTrusted] = useState(false);
  const trusted = isTrustedDevice() || serverTrusted;
  const backToQuick = () => { sessionStorage.removeItem("neec_full_login"); nav("/quick"); };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => localStorage.getItem("neec_offline_denied") ? (localStorage.removeItem("neec_offline_denied"), "Internet is required to sign in again. This register's offline session has expired.") : "");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [twofa, setTwofa] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await login(email, password, twofa ? code : undefined);
      if (res && res.twofaRequired) { setTwofa(true); setError(""); setLoading(false); return; }
      sessionStorage.removeItem("neec_full_login");
      nav("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 neec-grid-bg opacity-[0.07]" />
        <div className="relative flex items-center gap-2.5">
          <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center font-head font-extrabold text-xl">N</div>
          <div>
            <p className="font-head font-extrabold text-xl leading-none">NEEC POS</p>
            <p className="text-xs text-emerald-400 tracking-wider flex items-center gap-1"><Zap className="w-3 h-3" /> BUSINESS PLATFORM</p>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-head text-4xl xl:text-5xl font-extrabold leading-tight">
            One account.<br /><span className="text-emerald-400">Every part</span> of your business.
          </h1>
          <p className="text-slate-300 max-w-md">Point of sale, inventory, customer credit and receivables, built to grow into any industry without starting over.</p>
          <div className="space-y-3 max-w-md">
            {[
              { icon: TrendingUp, t: "Credit & Receivables Aging", d: "Track every dollar owed with live aging buckets." },
              { icon: Boxes, t: "Smart Inventory & Catalogs", d: "Preloaded catalog. Import what you sell." },
              { icon: ShieldCheck, t: "Manager PIN Controls", d: "Secure step-up approval for credit overrides." },
            ].map((f) => (
              <div key={f.t} className="flex gap-3 items-start bg-white/5 border border-white/10 rounded-xl p-3.5">
                <f.icon className="w-5 h-5 text-emerald-400 mt-0.5" />
                <div><p className="font-semibold text-sm">{f.t}</p><p className="text-xs text-slate-400">{f.d}</p></div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-500">© 2026 NEEC POS · Modular Business Management Platform</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-head font-extrabold">N</div>
            <p className="font-head font-extrabold text-lg">NEEC POS</p>
          </div>
          <h2 className="font-head text-3xl font-extrabold">Welcome back</h2>
          <p className="text-muted-foreground mt-1 mb-8">Sign in to your business dashboard.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="h-12 mt-1.5" data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider">Password</Label>
                <Link to="/forgot-password" className="text-xs text-emerald-500 hover:underline" data-testid="forgot-link">Forgot password?</Link>
              </div>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" className="h-12 mt-1.5" data-testid="login-password" />
            </div>
            {trusted && (
              <button type="button" onClick={backToQuick} className="text-xs text-violet-500 hover:underline flex items-center gap-1" data-testid="login-back-to-quick">← Back to Quick Login (staff PIN)</button>
            )}
            {twofa && (
              <div data-testid="login-2fa">
                <Label className="text-xs font-semibold uppercase tracking-wider">Authenticator Code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code or recovery code" className="h-12 mt-1.5" autoFocus data-testid="login-2fa-code" />
                <p className="text-xs text-muted-foreground mt-1">Enter the code from your authenticator app, or a one-time recovery code.</p>
              </div>
            )}
            {error && <p className="text-sm text-rose-500" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
